import { GroupIds } from "../models/enumerations/group-ids";
import GroupUtil from "./group-util";
import i18n from "../i18n";
import { MskScore } from "../models/interfaces/scores/msk-score";

const tEn = i18n.getFixedT("en");

export interface PartialMskScoreData {
    percentage: number;
    ageRange: string;
    isFemale: boolean;
    mskAverage: number;
    riskCode: string;
    focusAreas: Record<string, any>;
    otherGroupsWithPain: Record<string, any>;
}

const GROUP_PROPERTY_MAP: Record<string, string> = {
    [GroupIds.BALANCE]: "movementScore.balanceScore",
    [GroupIds.BALANCE_BREATHING]: "movementScore.balanceScore",
    [GroupIds.SQUAT]: "movementScore.squatScore",
    [GroupIds.SQUAT_BREATHING]: "movementScore.squatScore",
    [GroupIds.TOE_TOUCH]: "movementScore.toeTouchScore",
    [GroupIds.TOE_TOUCH_BREATHING]: "movementScore.toeTouchScore",
    [GroupIds.SHOULDER_MOBILITY]: "movementScore.shoulderMobilityScore",
    [GroupIds.SHOULDER_MOBILITY_BREATHING]: "movementScore.shoulderMobilityScore",
    [GroupIds.ROTATION]: "movementScore.rotationScore",
    [GroupIds.ROTATION_BREATHING]: "movementScore.rotationScore",
    [GroupIds.SPINE_CLEARING]: "SPECIAL_SPINE_CLEARING",
    [GroupIds.SPINE_CLEARING_BREATHING]: "SPECIAL_SPINE_CLEARING",
    [GroupIds.SHOULDER_CLEARING]: "SPECIAL_SHOULDER_CLEARING",
    [GroupIds.SHOULDER_CLEARING_BREATHING]: "SPECIAL_SHOULDER_CLEARING",
    [GroupIds.SLEEP]: "lifestyleScore.sleepScore",
    [GroupIds.BODY_COMPOSITION]: "lifestyleScore.bodyCompositionScore",
    [GroupIds.BREATHING]: "lifestyleScore.breathingScore",
    [GroupIds.INJURY_HISTORY]: "lifestyleScore.injuryHistoryScore",
    [GroupIds.NUTRITION]: "lifestyleScore.nutritionScore",
    [GroupIds.PHYSICAL_ACTIVITY]: "lifestyleScore.physicalActivityScore",
    [GroupIds.BEHAVIORAL_HEALTH]: "lifestyleScore.behavioralScore",
};

const FIELDS_TO_REMOVE = ["resultDescription", "score", "painSeverity"];

function sanitizeObject(obj: any): any {
    if (!obj || typeof obj !== "object") return obj;
    const sanitized = { ...obj };
    for (const field of FIELDS_TO_REMOVE) {
        delete sanitized[field];
    }
    return sanitized;
}

function getNestedValue(obj: any, path: string): any {
    if (!obj || !path) return undefined;
    const parts = path.split(".");
    let current = obj;
    for (const part of parts) {
        if (current === undefined || current === null) return undefined;
        current = current[part];
    }
    return current;
}

function extractRootFields(mskScore: MskScore): PartialMskScoreData {
    return {
        percentage: mskScore.percentage ?? 0,
        ageRange: mskScore.averageScores?.ageRange ?? "",
        isFemale: mskScore.averageScores?.isFemale ?? false,
        mskAverage: mskScore.averageScores?.mskAverage ?? 0,
        riskCode: mskScore.riskFactor?.riskCode ?? "",
        focusAreas: {},
        otherGroupsWithPain: {}
    };
}

function extractFocusAreas(mskScore: MskScore): Record<string, any> {
    if (!mskScore.focusAreas) return {};

    const focusAreasMap: Record<string, any> = {};
    const rawGroupIds: string[] = [];

    for (const f of mskScore.focusAreas) {
        if (f.groupId) {
            rawGroupIds.push(f.groupId);
        }
    }

    const actualMovementGroupId = mskScore.movementScore?.focusArea?.groupId;

    const resolvedGroupIds = [...rawGroupIds];
    if (actualMovementGroupId && !resolvedGroupIds.includes(actualMovementGroupId)) {
        resolvedGroupIds.push(actualMovementGroupId);
    }

    for (const groupId of rawGroupIds) {
        if (groupId === GroupIds.MOVEMENT_HEALTH || groupId === GroupIds.MOVEMENT_HEALTH_BREATHING) {
            if (actualMovementGroupId) {
                const propertyPath = GROUP_PROPERTY_MAP[actualMovementGroupId];
                if (propertyPath === "SPECIAL_SPINE_CLEARING") {
                    if (mskScore.movementScore) {
                        focusAreasMap[groupId] = {
                            movementGroupName: tEn(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(actualMovementGroupId)),
                            hasSpineClearingPain: mskScore.movementScore.hasSpineClearingPain,
                            spineClearingPainAreaAndScale: mskScore.movementScore.spineClearingPainAreaAndScale
                        };
                    }
                } else if (propertyPath === "SPECIAL_SHOULDER_CLEARING") {
                    if (mskScore.movementScore) {
                        focusAreasMap[groupId] = {
                            movementGroupName: tEn(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(actualMovementGroupId)),
                            hasShoulderClearingPain: mskScore.movementScore.hasShoulderClearingPain,
                            shoulderClearingPainAreaAndScale: mskScore.movementScore.shoulderClearingPainAreaAndScale
                        };
                    }
                } else if (propertyPath) {
                    const value = getNestedValue(mskScore, propertyPath);
                    if (value !== undefined) {
                        focusAreasMap[groupId] = sanitizeObject({ ...value, groupName: tEn(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(actualMovementGroupId)) });
                    }
                }
            }
            continue;
        }

        const propertyPath = GROUP_PROPERTY_MAP[groupId];

        if (propertyPath === "SPECIAL_SPINE_CLEARING") {
            if (mskScore.movementScore) {
                focusAreasMap[groupId] = {
                    movementGroupName: tEn(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(groupId)),
                    hasSpineClearingPain: mskScore.movementScore.hasSpineClearingPain,
                    spineClearingPainAreaAndScale: mskScore.movementScore.spineClearingPainAreaAndScale
                };
            }
        } else if (propertyPath === "SPECIAL_SHOULDER_CLEARING") {
            if (mskScore.movementScore) {
                focusAreasMap[groupId] = {
                    movementGroupName: tEn(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(groupId)),
                    hasShoulderClearingPain: mskScore.movementScore.hasShoulderClearingPain,
                    shoulderClearingPainAreaAndScale: mskScore.movementScore.shoulderClearingPainAreaAndScale
                };
            }
        } else if (propertyPath) {
            const value = getNestedValue(mskScore, propertyPath);
            if (value !== undefined) {
                focusAreasMap[groupId] = sanitizeObject({ ...value, groupName: tEn(GroupUtil.getNameTranslationReferenceIncludingMovementBreathing(groupId)) });
            }
        }
    }

    return focusAreasMap;
}

function extractOtherGroupsWithPain(mskScore: MskScore, focusAreaGroupIds: string[]): Record<string, any> {
    const otherGroupsWithPain: Record<string, any> = {};
    const focusSet = new Set(focusAreaGroupIds);

    const checkGroupHasPain = (groupId: string, data: any): boolean => {
        if (!data) return false;
        if (data.painAreasAndScales && data.painAreasAndScales.length > 0) return true;
        if (data.hasSpineClearingPain === true) return true;
        if (data.hasShoulderClearingPain === true) return true;
        return false;
    };

    if (mskScore.movementScore) {
        const ms = mskScore.movementScore;

        if (!focusSet.has(GroupIds.TOE_TOUCH) && !focusSet.has(GroupIds.TOE_TOUCH_BREATHING)) {
            if (checkGroupHasPain(GroupIds.TOE_TOUCH, ms.toeTouchScore)) {
                otherGroupsWithPain[GroupIds.TOE_TOUCH] = {
                    groupId: GroupIds.TOE_TOUCH,
                    painAreasAndScales: ms.toeTouchScore?.painAreasAndScales
                };
            }
        }

        if (!focusSet.has(GroupIds.SQUAT) && !focusSet.has(GroupIds.SQUAT_BREATHING)) {
            if (checkGroupHasPain(GroupIds.SQUAT, ms.squatScore)) {
                otherGroupsWithPain[GroupIds.SQUAT] = {
                    groupId: GroupIds.SQUAT,
                    painAreasAndScales: ms.squatScore?.painAreasAndScales
                };
            }
        }

        if (!focusSet.has(GroupIds.BALANCE) && !focusSet.has(GroupIds.BALANCE_BREATHING)) {
            if (checkGroupHasPain(GroupIds.BALANCE, ms.balanceScore)) {
                otherGroupsWithPain[GroupIds.BALANCE] = {
                    groupId: GroupIds.BALANCE,
                    painAreasAndScales: ms.balanceScore?.painAreasAndScales
                };
            }
        }

        if (!focusSet.has(GroupIds.ROTATION) && !focusSet.has(GroupIds.ROTATION_BREATHING)) {
            if (checkGroupHasPain(GroupIds.ROTATION, ms.rotationScore)) {
                otherGroupsWithPain[GroupIds.ROTATION] = {
                    groupId: GroupIds.ROTATION,
                    painAreasAndScales: ms.rotationScore?.painAreasAndScales
                };
            }
        }

        if (!focusSet.has(GroupIds.SHOULDER_MOBILITY) && !focusSet.has(GroupIds.SHOULDER_MOBILITY_BREATHING)) {
            if (checkGroupHasPain(GroupIds.SHOULDER_MOBILITY, ms.shoulderMobilityScore)) {
                otherGroupsWithPain[GroupIds.SHOULDER_MOBILITY] = {
                    groupId: GroupIds.SHOULDER_MOBILITY,
                    painAreasAndScales: ms.shoulderMobilityScore?.painAreasAndScales
                };
            }
        }

        if (!focusSet.has(GroupIds.SPINE_CLEARING) && !focusSet.has(GroupIds.SPINE_CLEARING_BREATHING)) {
            if (ms.hasSpineClearingPain) {
                otherGroupsWithPain[GroupIds.SPINE_CLEARING] = {
                    groupId: GroupIds.SPINE_CLEARING,
                    painAreasAndScales: ms.spineClearingPainAreaAndScale
                };
            }
        }

        if (!focusSet.has(GroupIds.SHOULDER_CLEARING) && !focusSet.has(GroupIds.SHOULDER_CLEARING_BREATHING)) {
            if (ms.hasShoulderClearingPain) {
                otherGroupsWithPain[GroupIds.SHOULDER_CLEARING] = {
                    groupId: GroupIds.SHOULDER_CLEARING,
                    painAreasAndScales: ms.shoulderClearingPainAreaAndScale
                };
            }
        }
    }

    if (mskScore.lifestyleScore?.injuryHistoryScore) {
        if (!focusSet.has(GroupIds.INJURY_HISTORY)) {
            if (checkGroupHasPain(GroupIds.INJURY_HISTORY, mskScore.lifestyleScore.injuryHistoryScore)) {
                otherGroupsWithPain[GroupIds.INJURY_HISTORY] = {
                    groupId: GroupIds.INJURY_HISTORY,
                    painAreasAndScales: mskScore.lifestyleScore.injuryHistoryScore?.painAreasAndScales
                };
            }
        }
    }

    return otherGroupsWithPain;
}

export function buildPartialMskScore(mskScore: MskScore): PartialMskScoreData {
    const partialData = extractRootFields(mskScore);

    const rawGroupIds: string[] = [];
    if (mskScore.focusAreas) {
        for (const f of mskScore.focusAreas) {
            if (f.groupId) {
                rawGroupIds.push(f.groupId);
            }
        }
    }

    const actualMovementGroupId = mskScore.movementScore?.focusArea?.groupId;

    const resolvedGroupIds = [...rawGroupIds];
    if (actualMovementGroupId && !resolvedGroupIds.includes(actualMovementGroupId)) {
        resolvedGroupIds.push(actualMovementGroupId);
    }

    partialData.focusAreas = extractFocusAreas(mskScore);

    partialData.otherGroupsWithPain = extractOtherGroupsWithPain(mskScore, resolvedGroupIds);

    return partialData;
}

export { GROUP_PROPERTY_MAP, getNestedValue };